import { type Location } from '@koala/sdk';
import { getPopularProducts, getWebConfig, getTags, getMenu, type Category } from '@koala/sdk/v4';
import { Hydrate, QueryClient } from '@tanstack/react-query';
import Router from 'next/router';
import { useEffect } from 'react';
import { END } from 'redux-saga';
import Layout from '@/components/app/layout/index';
import GenericErrorBoundary from '@/components/genericErrorBoundary';
import { StoreHead } from '@/components/store/pageTitle';
import { DELIVERY_TIME_WANTED_MODES, EDGE_NO_STORE_HEADER } from '@/constants/global';
import { LOCATION_STATUSES } from '@/constants/locations';
import { ROUTES } from '@/constants/routes';
import { LocationNotFound } from '@/features/store/location-not-found';
import { getMenuQueryKey, hasAvailableProducts } from '@/features/menu/service';
import { StoreDetails } from '@/features/store/details';
import { OrderInterruptedFallback } from '@/features/store/order-interrupted-fallback';
import { useDispatch, useSelector } from '@/redux';
import allergenActions from '@/redux/allergens/actions';
import { locationsActions } from '@/redux/locations/actions';
import { wrapper, type SagaStore } from '@/redux/store';
import { createHttpClientWithToken } from '@/services/client';
import { getFooterTemplate } from '@/services/footer.service';
import { fetchLocation, fetchLocationByBrandId } from '@/services/locations.service';
import { queryOnServer } from '@/services/ssr.service';
import { getOrigin } from '@/utils';
import { supportsBrandId } from '@/utils/config';
import {
  parseLocationRouteParams,
  toResolvedLocationId,
  type StoreRouteParams,
} from '@/utils/locations';
import { SSR_RETRY_OPTIONS } from '@/utils/requests';
import { isStatusError } from '@/utils/networkErrors';
import { retryGuardedRead } from '@/utils/withJsonGuard';
import OrderStatus from '@/components/checkout/orderStatus';
import { ENV } from '@/constants/envConfig';

interface Props {
  routeParams: StoreRouteParams;
  /** Absent on a client-side navigation, which skips the server prefetch batch. */
  serverState?: unknown;
  /** True when coo-api returned 404 for this store location. (TEAM-14936) */
  locationNotFound?: boolean;
}

const Store = ({ serverState, routeParams, locationNotFound }: Props) => {
  const { locations } = useSelector((state) => state.app);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(allergenActions.fetchAllergens());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (locationNotFound) {
    return (
      <Layout>
        <LocationNotFound />
      </Layout>
    );
  }

  return (
    <Hydrate state={serverState}>
      <StoreHead routeParams={routeParams} />

      <Layout
        // Don't render page if location is inactive
        disabled={locations.detail?.status_id === LOCATION_STATUSES.INACTIVE}
      >
        {/*
         * Keyed on the resolved location id so React remounts the boundary — and
         * so clears its `hasError` — when the diner switches stores.
         * `GenericErrorBoundary` sets `hasError` in `getDerivedStateFromError` and
         * never clears it, and this is a dynamic route: a client-side store→store
         * navigation keeps this page component mounted, so without a key a diner
         * who crashes on store A would still be looking at the fallback on store B.
         * `locations.detail.id` is hydrated by `hydrateLocation` inside
         * `getInitialProps` (a synchronous `put` of LOCATION_SUCCESS) on both the
         * server render and the client-side navigation, so it is defined on the
         * first render rather than flipping from `undefined` later. Keying on the
         * scalar id — not the `detail` object — also keeps a repeat
         * LOCATION_SUCCESS for the same store from remounting anything.
         */}
        <GenericErrorBoundary key={locations.detail?.id} fallbackUI={<OrderInterruptedFallback />}>
          {['dev', 'staging'].includes(ENV) ? <OrderStatus /> : null}
          <StoreDetails routeParams={routeParams} />
        </GenericErrorBoundary>
      </Layout>
    </Hydrate>
  );
};

Store.getInitialProps = wrapper.getInitialPageProps((store) => async ({ req, query, res }) => {
  /**
   * 🚨 REDUX SAGAS TRIGGERED SERVER-SIDE THAT CALL THE SDK WILL NOT WORK!
   * All SDK-dependant Sagas rely on `typeof window !== "undefined"` in
   * order to determine the request origin. This condition won't be met
   * server-side and the sagas will fail silently.
   *
   * If you need to make SDK requests server-side, move the fetch logic
   * directly into this function for now!
   *
   * @see https://github.com/Chowly/koala-ordering-webapp/pull/1786
   */

  let origin: string;
  if (typeof window !== 'undefined') {
    origin = getOrigin(window.location.host);
  } else if (req?.headers.host) {
    origin = getOrigin(req.headers.host);
  } else {
    throw new Error('Missing request origin');
  }

  const token = store.getState().app.global.token;
  const orgId = (store.getState().app.organization?.organization.id ?? null) as number | null;
  const cmsConfigState = store.getState().app.cmsConfig;

  const sdkClient = createHttpClientWithToken({ origin, token });
  const reactQueryClient = new QueryClient();

  const fetchFooter = () => getFooterTemplate({ origin, orgId, token });

  /**
   * The footer needs nothing but `origin`, `orgId` and `token`, all captured above
   * before the first `await`, so starting it now overlaps it with the location
   * fetch below instead of deferring it into the post-location batch. Its
   * arguments are identical either way, so the hoist cannot change what it
   * returns. Only started for a server render — a client-side navigation returns
   * before the batch and its footer is already cached. (TEAM-13663)
   */
  const footerPromise = req ? fetchFooter() : undefined;
  // The no-op catch stops a rejection landing during the awaits in between from
  // surfacing as an unhandled rejection. `queryOnServer` still reports it through
  // its own `Promise.allSettled` handling.
  footerPromise?.catch(() => undefined);

  /**
   * `cmsConfig.webConfig` is seeded with the SDK's `fallbackConfig` and is non-optional on
   * the slice type, so the `webConfigState ?? (await getWebConfig(...))` fallback that used
   * to sit here could never run — nullish-coalescing never saw a nullish left side.
   *
   * The condition that actually matters is whether the config was ever *loaded*.
   * `_app.getInitialProps` sets `fetchServer` only on success and its catch leaves the flag
   * false; `fetchConfigsSaga` does the same for `fetchClient`. When neither is set, this page
   * is running on `fallbackConfig`, whose `locations.location_id_source` is `'koala_id'` — so
   * `supportsBrandId` below returns false and a brand-id storefront takes the wrong branch,
   * parsing a brand id as an integer. That resolves to `NaN` (rejected by `assertResolvedId`,
   * redirecting the diner home) or, worse, to a number that resolves against the wrong id
   * namespace. Re-fetch instead, under the same SSR budget as the reads further down.
   *
   * On final failure this degrades to `fallbackConfig` rather than throwing. The read sits
   * outside the try/catch below, so letting it escape would turn a config blip into an error
   * page — which it is not today. Retrying first and then degrading exactly as before is
   * strictly an improvement with no new failure mode. (TEAM-14914)
   */
  let webConfig = cmsConfigState.webConfig;

  if (!cmsConfigState.fetchServer && !cmsConfigState.fetchClient) {
    try {
      webConfig = await retryGuardedRead(
        () => getWebConfig({ client: sdkClient, organizationId: orgId }),
        {
          route: ROUTES.STORE,
          urlHint: '/configurations/schema/v1/config/label/web-config',
        },
        SSR_RETRY_OPTIONS,
      );
    } catch {
      // Keep `fallbackConfig` — the same degraded render this page produced before the
      // retry existed. Already reported once by `retryGuardedRead`'s `onFinalFailure`.
    }
  }

  const routeParams = parseLocationRouteParams(query);

  let locationDetails: { data: Location };

  /**
   * Three invariants hold this wrap in place (TEAM-14914):
   *
   * 1. Both callees are GETs — `getLocation` / `getLocationByBrandId` both `.get()` in the
   *    SDK — so retrying them is legal under the "reads only, never mutations" rule in
   *    `docs/COO_API_FAILURE_HANDLING.md`.
   * 2. Unlike the reads further down, this one runs in BOTH runtimes: it sits above the
   *    `if (!req) return`, so Next also executes it in the browser on a client-side
   *    navigation in from `/stores`. Retries and Sentry reports fire in both.
   * 3. Non-transient failures must stay single-attempt AND unreported, or this page
   *    amplifies them into a Sentry flood — it has done so before. A 404 is not in
   *    `RETRYABLE_STATUSES` and is not wrapped by `withJsonGuard` (not 5xx, not a network
   *    error), and `assertResolvedId` throws a plain `Error`; neither is retried, and
   *    neither is an `UpstreamRequestError`/`NonJsonResponseError`, so `onFinalFailure`
   *    reports neither. Covered by tests — keep them passing.
   *
   * The `catch` below is left as-is on purpose: it redirects home on *any* error, not just
   * not-found. That is a separate defect, out of scope here.
   */
  try {
    if (supportsBrandId(webConfig)) {
      locationDetails = await retryGuardedRead(
        () => fetchLocationByBrandId(routeParams.id, { client: sdkClient }),
        {
          route: ROUTES.STORE,
          urlHint: '/v1/ordering/store-locations/brand',
          locationId: routeParams.id,
        },
        SSR_RETRY_OPTIONS,
      );
    } else {
      /** @TODO remove the parseInt once all locationIDs are strings. */
      locationDetails = await retryGuardedRead(
        () => fetchLocation({ id: parseInt(routeParams.id, 10) }, { client: sdkClient }),
        {
          route: ROUTES.STORE,
          urlHint: '/v1/ordering/store-locations',
          locationId: routeParams.id,
        },
        SSR_RETRY_OPTIONS,
      );
    }
  } catch (e) {
    /**
     * A 404 means the store exists as a URL but the backend cannot find it right now.
     * Show the diner a clear message inside the brand's chrome instead of silently
     * redirecting to the homepage. Non-404 errors (network, 5xx) still redirect —
     * they are transient and the homepage is the safest landing. (TEAM-14936)
     *
     * @see https://maxschmitt.me/posts/next-js-redirects
     */
    if (isStatusError(e, [404])) {
      // A store the backend can't find right now must not stay "not found" at the edge for up
      // to a day after it comes back. (TEAM-16180)
      res?.setHeader(EDGE_NO_STORE_HEADER.name, EDGE_NO_STORE_HEADER.value);
      return { locationNotFound: true, routeParams };
    }

    if (res) {
      res.writeHead(302, {
        Location: ROUTES.HOMEPAGE,
        'Cache-Control': 'no-cache',
      });
      res.end();
    } else {
      await Router.replace('/');
    }

    return {};
  }

  // Hydrate location details and fetch the location's menu
  store.dispatch(locationsActions.hydrateLocation(locationDetails.data));

  /**
   * Next runs this in the browser on a client-side navigation — the diner clicking
   * a location card on `/stores`. Everything below only feeds the dehydrated cache
   * that `<Hydrate>` reads on a server render, and running it here would build a
   * throwaway `QueryClient` and refetch the menu the locator already warmed via
   * `usePrefetchMenu`. Return instead and let the page's own hooks read the shared
   * cache: `useMenu`, `usePopularItems`, `useFoodHalls` and `useFooterQuery` all
   * key off the same entries. Mirrors `pages/order-confirmation/[[...id]].tsx`.
   * (TEAM-13663)
   */
  if (!req) {
    return { routeParams };
  }

  /**
   * The menu and popular-products reads depend only on the resolved location id,
   * not on redux or saga state, so start them before the saga drain below rather
   * than after it. The menu is the critical request for this page. Their query
   * keys are unchanged, so the dehydrated state still matches what the client
   * hydrates and reads. (TEAM-13663)
   */
  const menuPromise = retryGuardedRead(
    () => getMenu({ id: locationDetails.data.id }, { client: sdkClient }),
    {
      route: ROUTES.STORE,
      urlHint: '/menus',
      locationId: locationDetails.data.id?.toString(),
    },
    SSR_RETRY_OPTIONS,
  );
  menuPromise.catch(() => undefined);

  const popularProductsPromise = retryGuardedRead(
    () =>
      getPopularProducts(
        { locationId: locationDetails.data.id, maxItems: 15 },
        { client: sdkClient },
      ),
    {
      route: ROUTES.STORE,
      urlHint: '/v1/ordering/recommendations/products/popular',
      locationId: locationDetails.data.id?.toString(),
    },
    SSR_RETRY_OPTIONS,
  );
  popularProductsPromise.catch(() => undefined);

  store.dispatch(END);
  await (store as SagaStore).sagaTask?.toPromise();

  // Built from the same helper the client hooks use, so the dehydrated entry is always the one
  // `useMenu` reads back (TEAM-13663). The edge-cache check below reads the same entry.
  const menuQueryKey = getMenuQueryKey({
    id: locationDetails.data.id,
    ifModifiedSince: undefined,
    wantedAt: DELIVERY_TIME_WANTED_MODES.ASAP,
  });

  /**
   * Feed the already-started reads into the batch. Their query keys are unchanged,
   * so the dehydrated state still matches what the client hydrates.
   *
   * One deliberate consequence: because these `queryFn`s hand back a promise that
   * is already in flight, react-query's own retry can no longer re-invoke them —
   * a retry just re-awaits the same result. Their real budget is the
   * `SSR_RETRY_OPTIONS` inside `retryGuardedRead` (2 attempts, 150→400 ms), which
   * is the tight server-side budget `docs/COO_API_FAILURE_HANDLING.md` calls for;
   * the outer retry was the bare `new QueryClient()` default stacking on top of
   * it. (TEAM-13663)
   */
  let serverRequests = [
    {
      queryKey: ['site-footer'],
      // Started above so it overlapped the location fetch. The fallback is
      // unreachable — the client-side path returned before this batch was built —
      // but keeps the type honest without an assertion.
      queryFn: () => footerPromise ?? fetchFooter(),
    },
    {
      // Normalized the same way `usePopularItems` normalizes its own key, so the
      // dehydrated entry is the one that hook reads back. `getMenuQueryKey` does
      // this for the menu below. (TEAM-13663)
      queryKey: [
        'popular-items',
        { id: toResolvedLocationId(locationDetails.data.id), wantedAt: 'asap' },
      ],
      queryFn: () => popularProductsPromise,
    },
    {
      queryKey: menuQueryKey,
      // Embeds the categories only. `getMenu`'s second element is the raw compressed
      // response as a number array — 40–50% of the gzip page on the two stores measured
      // on 2026-10-01 — and nothing reads it here: `useMenu` exposes only `data[0]`, and
      // the basket's session-storage copy comes from its own request in
      // `refreshBasketMenuSaga`. The empty array keeps the tuple `useMenu` hydrates, as
      // the SDK's uncompressed path returns. (TEAM-15350)
      queryFn: () => menuPromise.then(([categories]): [Category[], number[]] => [categories, []]),
    },
  ];

  // Only try to fetch tags for food hall brands.
  const state = store.getState();
  if (state.app.cmsConfig.webConfig.admin.food_hall) {
    serverRequests = serverRequests.concat([
      {
        queryKey: ['food-halls'],
        queryFn: () =>
          retryGuardedRead(
            () => getTags({ type: 'filter' }, { client: sdkClient }),
            {
              route: ROUTES.STORE,
              urlHint: '/v1/tags',
              locationId: locationDetails.data.id?.toString(),
            },
            SSR_RETRY_OPTIONS,
          ),
      },
    ]);
  }

  const serverState = await queryOnServer(serverRequests, reactQueryClient);

  /**
   * The edge cache on the POPs keeps a store page for up to 24 hours and serves it to every
   * diner. A page whose menu has no products (built while the restaurant is closed, or with an
   * empty menu) shows "This location is not currently open", and kept that long it would still
   * say so hours after the restaurant opens. An inactive location renders nothing, and must
   * not outlive its reactivation either. Both are marked for the edge not to keep.
   *
   * A failed menu read stays cacheable on purpose: it is left out of the dehydrated state, so
   * the browser fetches the menu on mount and the page repairs itself, while re-rendering on
   * every visit would add coo-api load exactly when it is failing. (TEAM-16180)
   */
  const menuState = reactQueryClient.getQueryState<[Category[], number[]]>(menuQueryKey);
  const rendersNoProducts =
    menuState?.status === 'success' && !hasAvailableProducts(menuState.data?.[0] ?? []);
  if (rendersNoProducts || locationDetails.data.status_id === LOCATION_STATUSES.INACTIVE) {
    res?.setHeader(EDGE_NO_STORE_HEADER.name, EDGE_NO_STORE_HEADER.value);
  }

  /**
   * `getInitialProps` returns the props directly — unlike `getServerSideProps`,
   * there is no `props` wrapper to unwrap. Nesting `serverState` under one (added
   * in 75862d06d, 2024-10-08) meant the component below read it as `undefined` and
   * `<Hydrate>` hydrated nothing, so every store page threw away its server
   * prefetch and refetched the menu, popular items, footer and tags in the
   * browser. (TEAM-13663)
   */
  return { serverState, routeParams };
});

export default Store;
